import {
  CommentOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  RotateRightOutlined,
} from "@ant-design/icons"
import {
  Button,
  Card,
  DatePicker,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Select,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  type UploadProps,
} from "antd"
import dayjs from "dayjs"
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import styled from "styled-components"

import Flex from "#blocks/Flex"
import Uploader from "#blocks/Uploader"
import ArtworkEditableThumb, {
  ARTWORK_EDIT_SOURCE,
} from "#components/ArtworkTiles/ArtworkEditableThumb"
import { ArtworkLightbox } from "#components/ArtworkTiles/artworkTilePrimitives"
import { type PortfolioStageStatus } from "#customTypes/composedTypes/portfolioTypes"

import PortfolioStepStatusCell, {
  renderStepStatusTooltipContent,
} from "./PortfolioStepStatusCell"
import {
  type PortfolioMockProject,
  type PortfolioMockReference,
  type PortfolioMockStage,
} from "./usePortfolioDbStorage"

// Hover/focus-within container: reveals any descendant with [data-hover-btn]
// when the user hovers, focuses into the container, or focuses the button
// itself (keyboard/touch accessible).
const SHoverRevealContainer = styled.div`
  position: relative;
  display: inline-block;

  [data-hover-btn] {
    opacity: 0;
    transition: opacity 0.15s ease;
  }

  &:hover [data-hover-btn],
  &:focus-within [data-hover-btn],
  [data-hover-btn]:focus-visible {
    opacity: 1;
  }
`

const { Paragraph, Text } = Typography

/** Same pixel size as project row thumbnails in `PortfolioPhotoGrid`. */
const PROJECT_LIST_THUMBNAIL_PX = 56

interface PortfolioProjectTabsProps {
  selectedProject: PortfolioMockProject
  activeDetailTab: "items" | "references"
  setActiveDetailTab: (key: "items" | "references") => void
  statusOptions: Array<{ label: string; value: PortfolioMockStage["status"] }>
  statusColorMap: Record<PortfolioMockStage["status"], string>
  dragStage: { projectId: string; stageId: string } | null
  setDragStage: (value: { projectId: string; stageId: string } | null) => void
  stageDropTargetId: string | null
  setStageDropTargetId: (value: string | null) => void
  setProjectDropTargetId: (value: string | null) => void
  reorderStagesInProject: (
    projectId: string,
    sourceStageId: string,
    targetStageId: string
  ) => void
  deleteStage: (projectId: string, stageId: string) => void
  updateStage: (
    projectId: string,
    stageId: string,
    updates: Partial<
      Pick<
        PortfolioMockStage,
        | "label"
        | "dueDate"
        | "medium"
        | "notes"
        | "teacherComment"
        | "feedback"
        | "status"
        | "imageUrl"
        | "rejectionReason"
      >
    >
  ) => void
  getDueDateStyle: (
    dueDate?: string
  ) => { color: string; fontWeight: number } | undefined
  referenceInputRef: MutableRefObject<HTMLInputElement | null>
  handleAddReference: (event: React.ChangeEvent<HTMLInputElement>) => void
  updateProjectReference: (
    projectId: string,
    referenceId: string,
    updates: Partial<
      Pick<
        PortfolioMockReference,
        "title" | "notes" | "teacherComment" | "imageUrl"
      >
    >
  ) => void
  removeProjectReference: (projectId: string, referenceId: string) => void
  focusedStageId: string | null
  onFocusedStageHandled: () => void
  onAddStep: () => void
  onRemoveStageArtwork?: (artworkId: string) => void
  studentId?: string | null
  rotationByArtworkId?: Record<string, number>
  onRotateStageArtwork?: (artworkId: string) => void
  maxStepsReached?: boolean
  maxStepsPerProject?: number
}

const PortfolioProjectTabs = ({
  selectedProject,
  activeDetailTab,
  setActiveDetailTab,
  statusOptions,
  statusColorMap,
  dragStage,
  setDragStage,
  stageDropTargetId,
  setStageDropTargetId,
  setProjectDropTargetId,
  reorderStagesInProject,
  deleteStage,
  updateStage,
  getDueDateStyle,
  referenceInputRef,
  handleAddReference,
  updateProjectReference,
  removeProjectReference,
  focusedStageId,
  onFocusedStageHandled,
  onAddStep,
  onRemoveStageArtwork,
  studentId,
  rotationByArtworkId,
  onRotateStageArtwork,
  maxStepsReached = false,
  maxStepsPerProject,
}: PortfolioProjectTabsProps) => {
  const addStepDisabledTitle = maxStepsReached
    ? `Step limit reached${
        maxStepsPerProject ? ` (${maxStepsPerProject})` : ""
      }`
    : undefined
  const getStageRotation = useCallback(
    (stage: PortfolioMockStage): number => {
      if (!stage.artworkId) return 0
      return rotationByArtworkId?.[stage.artworkId] ?? 0
    },
    [rotationByArtworkId]
  )
  const [highlightedStageId, setHighlightedStageId] = useState<string | null>(
    null
  )
  const [lightboxTarget, setLightboxTarget] = useState<
    | { kind: "stage"; stageId: string }
    | { kind: "reference"; referenceId: string }
    | null
  >(null)
  const closeLightbox = useCallback(() => setLightboxTarget(null), [])
  const [selectedItemsStageId, setSelectedItemsStageId] = useState<
    string | null
  >(null)
  const [stageDropPosition, setStageDropPosition] = useState<
    "before" | "after" | null
  >(null)
  const [dueDatePickerOpen, setDueDatePickerOpen] = useState<string | null>(
    null
  )
  const [feedbackPrompt, setFeedbackPrompt] = useState<{
    projectId: string
    stageId: string
    status: PortfolioStageStatus
  } | null>(null)
  const [feedbackDraft, setFeedbackDraft] = useState("")

  useEffect(() => {
    setSelectedItemsStageId((prev) => {
      const stageIds = selectedProject.stages.map((s) => s.id)
      if (prev && stageIds.includes(prev)) return prev
      return selectedProject.stages[0]?.id ?? null
    })
  }, [selectedProject.id, selectedProject.stages])

  const selectedItemsStage: PortfolioMockStage | undefined = useMemo(() => {
    const match = selectedProject.stages.find(
      (s) => s.id === selectedItemsStageId
    )
    return match ?? selectedProject.stages[0]
  }, [selectedProject.stages, selectedItemsStageId])

  const selectItemsStage = useCallback((stageId: string) => {
    setSelectedItemsStageId(stageId)
  }, [])

  const extractUploadedUrl = (
    info: Parameters<NonNullable<UploadProps["onChange"]>>[0]
  ): string | undefined => {
    if (info.file.status !== "done") return undefined
    if (typeof info.file.response === "string") return info.file.response
    if (
      info.file.response &&
      typeof info.file.response === "object" &&
      "url" in info.file.response &&
      typeof info.file.response.url === "string"
    ) {
      return info.file.response.url
    }
    return undefined
  }

  useEffect(() => {
    if (activeDetailTab !== "items" || !focusedStageId) return
    selectItemsStage(focusedStageId)
    const stripElement = document.querySelector(
      `[data-stage-strip-id="${focusedStageId}"]`
    )
    if (stripElement instanceof HTMLElement) {
      stripElement.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      })
    }
    setHighlightedStageId(focusedStageId)
    onFocusedStageHandled()

    const timeoutId = window.setTimeout(() => {
      setHighlightedStageId((prev) => (prev === focusedStageId ? null : prev))
    }, 1800)

    return () => window.clearTimeout(timeoutId)
  }, [activeDetailTab, focusedStageId, onFocusedStageHandled, selectItemsStage])

  // Re-resolve the lightbox target from the live project on every render so
  // rotation and captions stay in sync with edits made while it's open, and
  // it closes itself (rather than crashing) if the underlying item is
  // deleted while open.
  const lightboxStage =
    lightboxTarget?.kind === "stage"
      ? selectedProject.stages.find((s) => s.id === lightboxTarget.stageId)
      : undefined
  const lightboxReference =
    lightboxTarget?.kind === "reference"
      ? (selectedProject.references ?? []).find(
          (r) => r.id === lightboxTarget.referenceId
        )
      : undefined
  const lightboxOpen =
    lightboxTarget !== null &&
    (lightboxStage !== undefined || lightboxReference !== undefined)
  const lightboxArtworkId = lightboxStage
    ? (lightboxStage.artworkId ?? lightboxStage.id)
    : (lightboxReference?.id ?? null)
  const lightboxSrc = lightboxStage
    ? lightboxStage.imageUrl
    : (lightboxReference?.imageUrl ?? "")
  const lightboxCaption = lightboxStage
    ? `${selectedProject.title} — ${lightboxStage.label}`
    : lightboxReference?.title?.trim() || "Reference"
  const lightboxRotation = lightboxStage ? getStageRotation(lightboxStage) : 0
  const lightboxActions =
    lightboxStage?.artworkId && onRotateStageArtwork ? (
      <Tooltip title="Rotate 90°">
        <Button
          icon={<RotateRightOutlined />}
          onClick={() =>
            onRotateStageArtwork(lightboxStage.artworkId as string)
          }
          aria-label="Rotate artwork 90 degrees"
        />
      </Tooltip>
    ) : undefined

  return (
    <>
      <Tabs
        activeKey={activeDetailTab}
        onChange={(key) => setActiveDetailTab(key as "items" | "references")}
        style={{ minWidth: 0, width: "100%", overflowX: "hidden" }}
        items={[
          {
            key: "items",
            label: "Items",
            children: (
              <Flex
                col
                gap={12}
                style={{ minWidth: 0, width: "100%", overflowX: "hidden" }}
              >
                {selectedProject.stages.length === 0 ? (
                  <Empty
                    description="No steps yet."
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  >
                    <Tooltip title={addStepDisabledTitle}>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={onAddStep}
                        disabled={maxStepsReached}
                      >
                        Add Step
                      </Button>
                    </Tooltip>
                  </Empty>
                ) : (
                  <>
                    <Flex
                      gap={8}
                      style={{
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                      }}
                    >
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        Drag steps to reorder
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {selectedProject.stages.length} steps
                      </Text>
                    </Flex>
                    <div
                      style={{
                        width: "100%",
                        minWidth: 0,
                        maxWidth: "100%",
                        overflowX: "auto",
                        overflowY: "hidden",
                        padding: 2,
                        paddingBottom: 8,
                        WebkitOverflowScrolling: "touch",
                        scrollSnapType: "x proximity",
                        scrollbarGutter: "stable",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          flexWrap: "nowrap",
                          gap: 0,
                          width: "max-content",
                        }}
                      >
                        {selectedProject.stages.map((stage, stepIndex) => {
                          const isStripSelected =
                            selectedItemsStage?.id === stage.id
                          const isReorderTarget =
                            stageDropTargetId === stage.id &&
                            !!dragStage &&
                            dragStage.projectId === selectedProject.id &&
                            dragStage.stageId !== stage.id
                          const isDraggedSource =
                            !!dragStage &&
                            dragStage.projectId === selectedProject.id &&
                            dragStage.stageId === stage.id

                          const stripRing =
                            isStripSelected || highlightedStageId === stage.id
                              ? isStripSelected
                                ? "0 0 0 2px #1677ff"
                                : "0 0 0 2px #91caff"
                              : "0 0 0 1px #f0f0f0"

                          return (
                            <Tooltip
                              key={stage.id}
                              title={renderStepStatusTooltipContent({
                                status: stage.status,
                                dueDate: stage.dueDate,
                                feedbackText: stage.feedback,
                              })}
                              mouseEnterDelay={0.2}
                            >
                              <div
                                data-stage-strip-id={stage.id}
                                draggable
                                role="button"
                                tabIndex={0}
                                onClick={() => selectItemsStage(stage.id)}
                                onKeyDown={(event) => {
                                  if (
                                    event.key !== "Enter" &&
                                    event.key !== " "
                                  )
                                    return
                                  event.preventDefault()
                                  selectItemsStage(stage.id)
                                }}
                                onDragStart={(event) => {
                                  setDragStage({
                                    projectId: selectedProject.id,
                                    stageId: stage.id,
                                  })
                                  event.dataTransfer.effectAllowed = "copyMove"
                                }}
                                onDragEnd={() => {
                                  setDragStage(null)
                                  setStageDropTargetId(null)
                                  setStageDropPosition(null)
                                  setProjectDropTargetId(null)
                                }}
                                onDragOver={(event) => {
                                  const canReorderWithinProject =
                                    !!dragStage &&
                                    dragStage.projectId ===
                                      selectedProject.id &&
                                    dragStage.stageId !== stage.id
                                  if (!canReorderWithinProject) return

                                  event.preventDefault()
                                  event.dataTransfer.dropEffect = "move"
                                  setStageDropTargetId(stage.id)
                                  const rect =
                                    event.currentTarget.getBoundingClientRect()
                                  setStageDropPosition(
                                    event.clientX < rect.left + rect.width / 2
                                      ? "before"
                                      : "after"
                                  )
                                }}
                                onDragLeave={() => {
                                  if (stageDropTargetId === stage.id) {
                                    setStageDropTargetId(null)
                                  }
                                  setStageDropPosition(null)
                                }}
                                onDrop={(event) => {
                                  const canReorderWithinProject =
                                    !!dragStage &&
                                    dragStage.projectId ===
                                      selectedProject.id &&
                                    dragStage.stageId !== stage.id
                                  if (!canReorderWithinProject) return

                                  event.preventDefault()

                                  const sourceIndex =
                                    selectedProject.stages.findIndex(
                                      (s) => s.id === dragStage.stageId
                                    )
                                  const targetIndex =
                                    selectedProject.stages.findIndex(
                                      (s) => s.id === stage.id
                                    )
                                  const dropAfter =
                                    stageDropPosition === "after"
                                  const needsAfterAdjust =
                                    dropAfter && sourceIndex < targetIndex
                                  const targetStage = needsAfterAdjust
                                    ? selectedProject.stages[targetIndex]
                                    : dropAfter &&
                                        targetIndex + 1 <
                                          selectedProject.stages.length
                                      ? selectedProject.stages[targetIndex + 1]
                                      : stage

                                  reorderStagesInProject(
                                    selectedProject.id,
                                    dragStage.stageId,
                                    targetStage.id
                                  )
                                  setStageDropTargetId(null)
                                  setStageDropPosition(null)
                                  setDragStage(null)
                                }}
                                style={{
                                  flex: "0 0 auto",
                                  scrollSnapAlign: "start",
                                  cursor: "pointer",
                                  opacity: isDraggedSource ? 0.45 : 1,
                                  transition: "opacity 0.15s ease",
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  gap: 2,
                                }}
                              >
                                <div
                                  style={{
                                    padding: 2,
                                    marginLeft:
                                      isReorderTarget &&
                                      stageDropPosition === "before"
                                        ? 0
                                        : 2,
                                    marginRight:
                                      isReorderTarget &&
                                      stageDropPosition === "after"
                                        ? 0
                                        : 2,
                                    marginTop: 2,
                                    marginBottom: 2,
                                    borderLeft:
                                      isReorderTarget &&
                                      stageDropPosition === "before"
                                        ? "3px solid #1677ff"
                                        : "3px solid transparent",
                                    borderRight:
                                      isReorderTarget &&
                                      stageDropPosition === "after"
                                        ? "3px solid #1677ff"
                                        : "3px solid transparent",
                                    borderRadius: 0,
                                    transition:
                                      "border-color 0.1s ease, margin 0.1s ease",
                                  }}
                                >
                                  <div
                                    style={{
                                      position: "relative",
                                      width: PROJECT_LIST_THUMBNAIL_PX,
                                      height: PROJECT_LIST_THUMBNAIL_PX,
                                      borderRadius: 6,
                                      overflow: "hidden",
                                      background: "#ffffff",
                                      boxShadow: stripRing,
                                    }}
                                  >
                                    {stage.imageUrl?.trim() ? (
                                      studentId && stage.artworkId ? (
                                        <ArtworkEditableThumb
                                          studentId={studentId}
                                          artworkId={stage.artworkId}
                                          source={ARTWORK_EDIT_SOURCE.PORTFOLIO}
                                          fill
                                          objectFit="cover"
                                          previewUrl={stage.imageUrl}
                                          previewRotation={getStageRotation(
                                            stage
                                          )}
                                          alt=""
                                          showTileActions={false}
                                          openOnClick={false}
                                        />
                                      ) : (
                                        <img
                                          alt=""
                                          src={stage.imageUrl}
                                          style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            objectPosition: "center",
                                            display: "block",
                                            transform: `rotate(${getStageRotation(stage)}deg)`,
                                            transition: "transform 0.2s",
                                          }}
                                        />
                                      )
                                    ) : (
                                      <div
                                        style={{
                                          width: PROJECT_LIST_THUMBNAIL_PX,
                                          height: PROJECT_LIST_THUMBNAIL_PX,
                                          background: "#f5f5f5",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <Text
                                          type="secondary"
                                          style={{
                                            fontSize: 10,
                                            lineHeight: 1,
                                            userSelect: "none",
                                          }}
                                        >
                                          No img
                                        </Text>
                                      </div>
                                    )}
                                    <Text
                                      style={{
                                        position: "absolute",
                                        top: 2,
                                        left: 2,
                                        margin: 0,
                                        fontSize: 9,
                                        lineHeight: "14px",
                                        color: "#fff",
                                        background: "rgba(0, 0, 0, 0.52)",
                                        padding: "0 4px",
                                        borderRadius: 2,
                                      }}
                                    >
                                      {stepIndex + 1}
                                    </Text>
                                    <div
                                      style={{
                                        position: "absolute",
                                        right: 2,
                                        bottom: 2,
                                        boxShadow: "0 0 0 1.5px #ffffff",
                                        borderRadius: 4,
                                        background: "#ffffff",
                                        lineHeight: 0,
                                      }}
                                    >
                                      <PortfolioStepStatusCell
                                        status={stage.status}
                                        withTooltip={false}
                                      />
                                    </div>
                                  </div>
                                </div>
                                <Text
                                  type="secondary"
                                  ellipsis
                                  style={{
                                    fontSize: 10,
                                    lineHeight: "13px",
                                    width: PROJECT_LIST_THUMBNAIL_PX,
                                    textAlign: "center",
                                    userSelect: "none",
                                  }}
                                >
                                  {stage.label}
                                </Text>
                              </div>
                            </Tooltip>
                          )
                        })}
                        <Tooltip title="Add step">
                          <div
                            role="button"
                            tabIndex={maxStepsReached ? -1 : 0}
                            aria-disabled={maxStepsReached}
                            title={addStepDisabledTitle}
                            onClick={() => {
                              if (maxStepsReached) return
                              onAddStep()
                            }}
                            onKeyDown={(event) => {
                              if (event.key !== "Enter" && event.key !== " ")
                                return
                              event.preventDefault()
                              if (maxStepsReached) return
                              onAddStep()
                            }}
                            style={{
                              flex: "0 0 auto",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 2,
                              cursor: maxStepsReached
                                ? "not-allowed"
                                : "pointer",
                              opacity: maxStepsReached ? 0.4 : 1,
                            }}
                          >
                            <div style={{ padding: 2, margin: 2 }}>
                              <div
                                style={{
                                  width: PROJECT_LIST_THUMBNAIL_PX,
                                  height: PROJECT_LIST_THUMBNAIL_PX,
                                  borderRadius: 6,
                                  border: "1px dashed #d9d9d9",
                                  background: "#fafafa",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <PlusOutlined
                                  style={{ fontSize: 18, color: "#8c8c8c" }}
                                />
                              </div>
                            </div>
                            <Text
                              type="secondary"
                              style={{
                                fontSize: 10,
                                lineHeight: "13px",
                                userSelect: "none",
                              }}
                            >
                              Add
                            </Text>
                          </div>
                        </Tooltip>
                      </div>
                    </div>
                    {selectedItemsStage ? (
                      <div
                        data-stage-id={selectedItemsStage.id}
                        style={{
                          width: "100%",
                          maxWidth: "100%",
                          minWidth: 0,
                          overflowX: "hidden",
                        }}
                      >
                        {(() => {
                          const stage = selectedItemsStage
                          const isDraggedSource =
                            !!dragStage &&
                            dragStage.projectId === selectedProject.id &&
                            dragStage.stageId === stage.id

                          return (
                            <div
                              style={{
                                borderLeft:
                                  highlightedStageId === stage.id
                                    ? "3px solid #1677ff"
                                    : "3px solid transparent",
                                borderTop: "1px solid #f0f0f0",
                                paddingTop: 12,
                                paddingLeft: 10,
                                minHeight: 330,
                                maxWidth: "100%",
                                boxSizing: "border-box",
                                opacity: isDraggedSource ? 0.45 : 1,
                                transition:
                                  "opacity 0.15s ease, border-color 0.15s ease",
                              }}
                            >
                              <Flex
                                col
                                gap={8}
                                style={{ minWidth: 0, maxWidth: "100%" }}
                              >
                                <Flex
                                  gap={8}
                                  style={{
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    minWidth: 0,
                                  }}
                                >
                                  <Paragraph
                                    editable={{
                                      onChange: (value) =>
                                        updateStage(
                                          selectedProject.id,
                                          stage.id,
                                          { label: value }
                                        ),
                                    }}
                                    strong
                                    style={{
                                      flex: "1 1 160px",
                                      fontSize: 14,
                                      minWidth: 0,
                                      marginBottom: 0,
                                    }}
                                  >
                                    {stage.label}
                                  </Paragraph>
                                  {dueDatePickerOpen === stage.id ||
                                  !stage.dueDate ? (
                                    <DatePicker
                                      value={
                                        stage.dueDate
                                          ? dayjs(stage.dueDate)
                                          : null
                                      }
                                      onChange={(date) => {
                                        updateStage(
                                          selectedProject.id,
                                          stage.id,
                                          {
                                            dueDate:
                                              date?.toISOString() ??
                                              stage.dueDate,
                                          }
                                        )
                                        setDueDatePickerOpen(null)
                                      }}
                                      size="small"
                                      placeholder="Due date"
                                      open={dueDatePickerOpen === stage.id}
                                      onOpenChange={(open) => {
                                        if (open) {
                                          setDueDatePickerOpen(stage.id)
                                        } else {
                                          setDueDatePickerOpen(null)
                                        }
                                      }}
                                    />
                                  ) : (
                                    <Text
                                      type="secondary"
                                      style={{
                                        fontSize: 13,
                                        cursor: "pointer",
                                        whiteSpace: "nowrap",
                                        ...getDueDateStyle(stage.dueDate),
                                      }}
                                      onClick={() =>
                                        setDueDatePickerOpen(stage.id)
                                      }
                                    >
                                      {stage.dueDate
                                        ? `Due ${dayjs(stage.dueDate).format("MMM D, YYYY")}`
                                        : "No due date"}
                                    </Text>
                                  )}
                                  <Select
                                    options={statusOptions}
                                    size="small"
                                    style={{ width: 150 }}
                                    value={stage.status}
                                    onChange={(status) => {
                                      const existingFeedback =
                                        stage.feedback?.trim()
                                      if (
                                        status === "has_feedback" &&
                                        !existingFeedback
                                      ) {
                                        setFeedbackDraft("")
                                        setFeedbackPrompt({
                                          projectId: selectedProject.id,
                                          stageId: stage.id,
                                          status: "has_feedback",
                                        })
                                        return
                                      }
                                      updateStage(
                                        selectedProject.id,
                                        stage.id,
                                        { status }
                                      )
                                    }}
                                    optionRender={(option) => (
                                      <Tag
                                        color={
                                          statusColorMap[
                                            (option.value as PortfolioMockStage["status"]) ??
                                              "not_started"
                                          ]
                                        }
                                      >
                                        {option.label}
                                      </Tag>
                                    )}
                                    labelRender={({ value, label }) => (
                                      <Tag
                                        color={
                                          statusColorMap[
                                            (value as PortfolioMockStage["status"]) ??
                                              "not_started"
                                          ]
                                        }
                                        style={{ marginInlineEnd: 0 }}
                                      >
                                        {label}
                                      </Tag>
                                    )}
                                  />
                                  <Popconfirm
                                    title="Are you sure?"
                                    okText="Yes, delete"
                                    cancelText="No"
                                    onConfirm={() =>
                                      deleteStage(selectedProject.id, stage.id)
                                    }
                                  >
                                    <Button
                                      icon={<DeleteOutlined />}
                                      size="small"
                                      type="text"
                                      danger
                                      aria-label="Delete step"
                                    />
                                  </Popconfirm>
                                </Flex>

                                <Flex
                                  gap={16}
                                  style={{
                                    alignItems: "flex-start",
                                    width: "100%",
                                    maxWidth: "100%",
                                    minWidth: 0,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <Flex
                                    center
                                    style={{
                                      flex: "1 1 300px",
                                      minWidth: 0,
                                      maxWidth: "100%",
                                      height: 300,
                                      overflow: "hidden",
                                    }}
                                  >
                                    {stage.imageUrl?.trim() ? (
                                      (() => {
                                        const rotation = getStageRotation(stage)
                                        const isLandscape = rotation % 180 === 0
                                        const linked =
                                          !!stage.artworkId && !!studentId
                                        const canRotate =
                                          !!stage.artworkId &&
                                          !!onRotateStageArtwork &&
                                          !linked
                                        return (
                                          <SHoverRevealContainer>
                                            {linked ? (
                                              <div
                                                style={{
                                                  position: "relative",
                                                  width: "100%",
                                                  height: 280,
                                                }}
                                              >
                                                <ArtworkEditableThumb
                                                  studentId={studentId ?? ""}
                                                  artworkId={
                                                    stage.artworkId as string
                                                  }
                                                  source={
                                                    ARTWORK_EDIT_SOURCE.PORTFOLIO
                                                  }
                                                  fill
                                                  objectFit="contain"
                                                  previewUrl={stage.imageUrl}
                                                  previewRotation={rotation}
                                                  alt={`${selectedProject.title} ${stage.label}`}
                                                />
                                              </div>
                                            ) : (
                                              <img
                                                key={stage.imageUrl}
                                                alt={`${selectedProject.title} ${stage.label}`}
                                                src={stage.imageUrl}
                                                onClick={() =>
                                                  setLightboxTarget({
                                                    kind: "stage",
                                                    stageId: stage.id,
                                                  })
                                                }
                                                style={{
                                                  maxHeight: isLandscape
                                                    ? 300
                                                    : 210,
                                                  maxWidth: isLandscape
                                                    ? "100%"
                                                    : 210,
                                                  width: "auto",
                                                  height: "auto",
                                                  borderRadius: 4,
                                                  objectFit: "contain",
                                                  cursor: "zoom-in",
                                                  transform: `rotate(${rotation}deg)`,
                                                  transition: "transform 0.2s",
                                                }}
                                              />
                                            )}
                                            {canRotate && (
                                              <Button
                                                data-hover-btn
                                                icon={<RotateRightOutlined />}
                                                size="small"
                                                type="default"
                                                onClick={() =>
                                                  onRotateStageArtwork(
                                                    stage.artworkId as string
                                                  )
                                                }
                                                title="Rotate 90°"
                                                aria-label="Rotate artwork 90 degrees"
                                                style={{
                                                  position: "absolute",
                                                  top: 6,
                                                  right: 40,
                                                }}
                                              />
                                            )}
                                            <Popconfirm
                                              title="Remove this image?"
                                              onConfirm={() => {
                                                updateStage(
                                                  selectedProject.id,
                                                  stage.id,
                                                  { imageUrl: "" }
                                                )
                                                if (
                                                  stage.artworkId &&
                                                  onRemoveStageArtwork
                                                ) {
                                                  onRemoveStageArtwork(
                                                    stage.artworkId
                                                  )
                                                }
                                              }}
                                              okText="Remove"
                                              okButtonProps={{ danger: true }}
                                            >
                                              <Button
                                                data-hover-btn
                                                danger
                                                icon={<DeleteOutlined />}
                                                size="small"
                                                type="primary"
                                                aria-label="Remove image"
                                                style={{
                                                  position: "absolute",
                                                  top: 6,
                                                  right: 6,
                                                }}
                                              />
                                            </Popconfirm>
                                          </SHoverRevealContainer>
                                        )
                                      })()
                                    ) : (
                                      <Uploader
                                        onStatusChange={(info) => {
                                          const nextUrl =
                                            extractUploadedUrl(info)
                                          if (!nextUrl) return
                                          updateStage(
                                            selectedProject.id,
                                            stage.id,
                                            {
                                              imageUrl: nextUrl,
                                            }
                                          )
                                        }}
                                        onDelete={() => {
                                          updateStage(
                                            selectedProject.id,
                                            stage.id,
                                            {
                                              imageUrl: "",
                                            }
                                          )
                                        }}
                                        folder={`portfolio/${selectedProject.id}/stages`}
                                        filename={stage.id}
                                        metadata={{
                                          projectId: selectedProject.id,
                                          stageId: stage.id,
                                          scope: "portfolio-stage",
                                        }}
                                      />
                                    )}
                                  </Flex>

                                  <Flex
                                    col
                                    gap={8}
                                    style={{
                                      flex: "1 1 300px",
                                      minWidth: 0,
                                      maxWidth: "100%",
                                    }}
                                  >
                                    <Flex col gap={10}>
                                      {(() => {
                                        const feedbackText =
                                          stage.feedback?.trim()
                                        if (!feedbackText) {
                                          return (
                                            <div>
                                              <Button
                                                type="dashed"
                                                size="small"
                                                icon={<CommentOutlined />}
                                                onClick={() => {
                                                  setFeedbackDraft("")
                                                  setFeedbackPrompt({
                                                    projectId:
                                                      selectedProject.id,
                                                    stageId: stage.id,
                                                    status: "has_feedback",
                                                  })
                                                }}
                                              >
                                                Leave feedback
                                              </Button>
                                            </div>
                                          )
                                        }
                                        return (
                                          <div
                                            style={{
                                              border: "1px solid #ffe58f",
                                              background: "#fffbe6",
                                              borderRadius: 6,
                                              padding: "8px 10px",
                                            }}
                                          >
                                            <Flex
                                              gap={6}
                                              style={{ alignItems: "center" }}
                                            >
                                              <CommentOutlined
                                                style={{ color: "#d48806" }}
                                              />
                                              <Text
                                                strong
                                                style={{ fontSize: 12 }}
                                              >
                                                Feedback
                                              </Text>
                                              <div style={{ flex: 1 }} />
                                              <Button
                                                icon={<EditOutlined />}
                                                size="small"
                                                type="text"
                                                aria-label="Edit feedback"
                                                onClick={() => {
                                                  setFeedbackDraft(feedbackText)
                                                  setFeedbackPrompt({
                                                    projectId:
                                                      selectedProject.id,
                                                    stageId: stage.id,
                                                    status: stage.status,
                                                  })
                                                }}
                                              />
                                              <Popconfirm
                                                title="Delete this feedback?"
                                                okText="Delete"
                                                okButtonProps={{ danger: true }}
                                                cancelText="Cancel"
                                                onConfirm={() =>
                                                  updateStage(
                                                    selectedProject.id,
                                                    stage.id,
                                                    { feedback: "" }
                                                  )
                                                }
                                              >
                                                <Button
                                                  icon={<DeleteOutlined />}
                                                  size="small"
                                                  type="text"
                                                  danger
                                                  aria-label="Delete feedback"
                                                />
                                              </Popconfirm>
                                            </Flex>
                                            <Paragraph
                                              style={{
                                                marginTop: 4,
                                                marginBottom: 0,
                                                whiteSpace: "pre-wrap",
                                                fontSize: 13,
                                              }}
                                            >
                                              {feedbackText}
                                            </Paragraph>
                                          </div>
                                        )
                                      })()}
                                      <div>
                                        <Text
                                          type="secondary"
                                          style={{
                                            fontSize: 12,
                                            display: "block",
                                            marginBottom: 4,
                                          }}
                                        >
                                          Medium
                                        </Text>
                                        <Paragraph
                                          editable={{
                                            onChange: (value) =>
                                              updateStage(
                                                selectedProject.id,
                                                stage.id,
                                                { medium: value }
                                              ),
                                          }}
                                          style={{
                                            marginBottom: 0,
                                            marginTop: 4,
                                          }}
                                        >
                                          {stage.medium || ""}
                                        </Paragraph>
                                      </div>
                                      <div>
                                        <Text
                                          type="secondary"
                                          style={{
                                            fontSize: 12,
                                            display: "block",
                                            marginBottom: 4,
                                          }}
                                        >
                                          Notes
                                        </Text>
                                        <Paragraph
                                          editable={{
                                            onChange: (value) =>
                                              updateStage(
                                                selectedProject.id,
                                                stage.id,
                                                { notes: value }
                                              ),
                                            autoSize: {
                                              minRows: 2,
                                              maxRows: 8,
                                            },
                                          }}
                                          style={{
                                            marginBottom: 0,
                                            marginTop: 4,
                                            whiteSpace: "pre-wrap",
                                          }}
                                        >
                                          {stage.notes ?? ""}
                                        </Paragraph>
                                      </div>
                                      {/* teacherComment, feedback, and rejectionReason
                                         fields hidden — not persisted to Firestore yet
                                         (portfolioConversions strips them on round-trip). */}
                                    </Flex>
                                  </Flex>
                                </Flex>
                              </Flex>
                            </div>
                          )
                        })()}
                      </div>
                    ) : null}
                  </>
                )}
              </Flex>
            ),
          },
          {
            key: "references",
            label: "References",
            children: (
              <Flex col gap={12}>
                <Flex gap={8} style={{ alignItems: "center" }}>
                  <Button
                    onClick={() => referenceInputRef.current?.click()}
                    size="small"
                    type="default"
                  >
                    Add Reference
                  </Button>
                  <input
                    accept="image/*"
                    onChange={handleAddReference}
                    ref={referenceInputRef}
                    style={{ display: "none" }}
                    type="file"
                  />
                  <Text type="secondary" style={{ fontSize: 14 }}>
                    {selectedProject.references?.length ?? 0} reference items
                  </Text>
                </Flex>
                <Flex col gap={10}>
                  {(selectedProject.references ?? []).map((reference) => (
                    <Card
                      key={reference.id}
                      size="small"
                      styles={{ body: { padding: 10 } }}
                    >
                      <Flex gap={12} style={{ alignItems: "flex-start" }}>
                        <Flex
                          center
                          style={{
                            width: 240,
                            minWidth: 240,
                            height: 240,
                            border: "1px solid #f0f0f0",
                            borderRadius: 4,
                            overflow: "hidden",
                          }}
                        >
                          {reference.imageUrl?.trim() ? (
                            <img
                              alt={reference.title || "Reference"}
                              src={reference.imageUrl}
                              onClick={() =>
                                setLightboxTarget({
                                  kind: "reference",
                                  referenceId: reference.id,
                                })
                              }
                              style={{
                                maxHeight: "100%",
                                maxWidth: "100%",
                                objectFit: "contain",
                                cursor: "zoom-in",
                              }}
                            />
                          ) : (
                            <Uploader
                              onStatusChange={(info) => {
                                const nextUrl = extractUploadedUrl(info)
                                if (!nextUrl) return
                                updateProjectReference(
                                  selectedProject.id,
                                  reference.id,
                                  {
                                    imageUrl: nextUrl,
                                  }
                                )
                              }}
                              onDelete={() => {
                                updateProjectReference(
                                  selectedProject.id,
                                  reference.id,
                                  {
                                    imageUrl: "",
                                  }
                                )
                              }}
                              folder={`portfolio/${selectedProject.id}/references`}
                              filename={reference.id}
                              metadata={{
                                projectId: selectedProject.id,
                                referenceId: reference.id,
                                scope: "portfolio-reference",
                              }}
                            />
                          )}
                        </Flex>
                        <Flex col gap={8} style={{ flex: 1, minWidth: 280 }}>
                          <Input
                            value={reference.title ?? ""}
                            placeholder="Reference title"
                            onChange={(event) =>
                              updateProjectReference(
                                selectedProject.id,
                                reference.id,
                                {
                                  title: event.target.value,
                                }
                              )
                            }
                          />
                          <Input.TextArea
                            value={reference.notes ?? ""}
                            placeholder="Reference notes"
                            autoSize={{ minRows: 2, maxRows: 5 }}
                            onChange={(event) =>
                              updateProjectReference(
                                selectedProject.id,
                                reference.id,
                                {
                                  notes: event.target.value,
                                }
                              )
                            }
                          />
                          {/* Reference teacherComment hidden — not persisted to Firestore yet. */}
                          <Flex style={{ justifyContent: "flex-end" }}>
                            <Button
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() =>
                                removeProjectReference(
                                  selectedProject.id,
                                  reference.id
                                )
                              }
                              size="small"
                              type="text"
                            >
                              Remove
                            </Button>
                          </Flex>
                        </Flex>
                      </Flex>
                    </Card>
                  ))}
                  {!selectedProject.references?.length && (
                    <Empty
                      description="No reference items yet."
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  )}
                </Flex>
              </Flex>
            ),
          },
        ]}
      />
      {/* Rejection reason modal hidden — rejectionReason not persisted to Firestore yet. */}
      <Modal
        title="Leave feedback"
        open={!!feedbackPrompt}
        okText="Save feedback"
        okButtonProps={{ disabled: !feedbackDraft.trim() }}
        onOk={() => {
          const trimmed = feedbackDraft.trim()
          if (!feedbackPrompt || !trimmed) return
          updateStage(feedbackPrompt.projectId, feedbackPrompt.stageId, {
            status: feedbackPrompt.status,
            feedback: trimmed,
          })
          setFeedbackPrompt(null)
          setFeedbackDraft("")
        }}
        onCancel={() => {
          setFeedbackPrompt(null)
          setFeedbackDraft("")
        }}
        destroyOnClose
      >
        <Text type="secondary" style={{ fontSize: 13 }}>
          Tell the student what needs to change before this step can progress.
        </Text>
        <Input.TextArea
          autoFocus
          value={feedbackDraft}
          onChange={(event) => setFeedbackDraft(event.target.value)}
          placeholder="Enter feedback (required)"
          autoSize={{ minRows: 3, maxRows: 8 }}
          style={{ marginTop: 8 }}
        />
      </Modal>
      <ArtworkLightbox
        open={lightboxOpen}
        artworkId={lightboxArtworkId}
        src={lightboxSrc}
        caption={lightboxCaption}
        rotation={lightboxRotation}
        animateRotation={!!lightboxStage}
        onClose={closeLightbox}
        actions={lightboxActions}
      />
    </>
  )
}

export default PortfolioProjectTabs
